import { Grid, TextField, Divider, Typography } from '@mui/material';
import CurrencySelector from 'components/CurrencySelector';
import ActionSelector from 'components/new/ActionSelector';
import CounterpartySelector from 'components/new/CounterpartySelector';
import Toggle from 'components/new/Toggle';
import VatRateSelector from 'components/new/VatRateSelector';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionRef } from 'further-types/lp/action';
import {
  IncomeOrExpenseState,
  IncomeOrExpenseType,
} from 'further-types/lp/income-and-expenses';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import StaticField from 'further-ui/components/StaticField';
import { PeriodReviewStatus } from 'further-types/lp/accounting-period';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useFundingSourceOptions from 'hooks/form/useFundingSourceOptions';
import api from 'lib/trpcClient';
import { FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import AiFieldHintController from '../AiFieldHintController';
import { useAiFieldHint, useAiFormFill } from '../AiFormFillContext';
import { FormValues } from '../FormWrapper';
import useSuggestIncomeAndExpense from '../hooks/useSuggestIncomeAndExpense';
import LedgerDateInput from './LedgerDateInput';
import InvoiceUploadSection from './InvoiceUploadSection';
import AiFieldHintWrapper from 'components/AiFieldHintWrapper';
import { maxBy } from 'lodash';
import { addDays } from 'date-fns';

const buildActionOptionsName = (action: {
  actionRef: ActionRef;
  asset: {
    name: string;
  };
  ledgerDate: CalendarDay;
}) => {
  switch (action.actionRef) {
    case ActionRef.Investment:
      return `Investment into ${
        action.asset?.name
      } on ${action.ledgerDate.toLabel()}`;
    case ActionRef.Realisation:
      return `Realisation of ${
        action.asset?.name
      } on ${action.ledgerDate.toLabel()}`;
    default:
      return `Unknown action type: ${action.actionRef}`;
  }
};

type Props = {
  id?: string;
  isSwitchMode?: boolean;
  vehicleId: string;
  incomeOrExpense: IncomeOrExpenseType | '';
  transactionCurrency: CurrencyCode;
};

const InputDetailsForm: FC<Props> = ({
  id,
  isSwitchMode,
  incomeOrExpense,
  transactionCurrency,
  vehicleId,
}) => {
  const { availableVehicles } = useLayoutContext();
  const { control, setValue, watch, resetField, getValues } =
    useFormContext<FormValues>();

  const { fetchSuggestedIncomeOrExpense, syncSuggestedIncomeOrExpense } =
    useSuggestIncomeAndExpense({
      vehicleId,
    });

  // Only needed for the composite Ledger date wrapper (no Controller)
  const startDateHint = useAiFieldHint('startDate');
  const apportionmentModeHint = useAiFieldHint('apportionmentMode');
  const endDateHint = useAiFieldHint('endDate');
  const { registerDependentFieldHandlers, isAiScanning } = useAiFormFill();

  const [contacts] = api.contact.list.useSuspenseQuery();
  const [actions] = api.action.listLinkableActions.useSuspenseQuery({
    vehicleId,
  });
  const [typeOptions] = api.incomeAndExpenses.getTypeOptions.useSuspenseQuery({
    vehicleId,
  });
  const [periods] = api.accountingPeriod.listPeriods.useSuspenseQuery({
    vehicleId,
  });

  const latestApprovedPeriod = maxBy(
    periods.filter((p) => p.reviewStatus === PeriodReviewStatus.Approved),
    (p) => p.periodEnd.toUTCDate().getTime(),
  );

  const switchDateMinDate = latestApprovedPeriod
    ? addDays(latestApprovedPeriod.periodEnd.toUTCDate(), 1)
    : undefined;

  const category = watch('category');

  const onCounterpartyChange = async (counterparty: {
    id?: string;
    name: string;
  }) => {
    const suggestedIncomeOrExpense = await fetchSuggestedIncomeOrExpense(
      counterparty,
    );

    syncSuggestedIncomeOrExpense({
      suggestedIncomeOrExpense: suggestedIncomeOrExpense ?? null,
      counterparty,
      setValue,
    });
  };

  useEffect(() => {
    registerDependentFieldHandlers({
      onCategoryChange: (value) => {
        setValue('category', value as any, {
          shouldValidate: false,
          shouldDirty: true,
        });
        getValues().apportionmentPeriods?.forEach((_, index) =>
          resetField(`apportionmentPeriods.${index}.transactions`),
        );
        resetField('incomeOrExpenseType');
        resetField('incomeOrExpenseState');
      },
      onIncomeOrExpenseStateChange: (value) => {
        setValue('incomeOrExpenseState', value as any, {
          shouldValidate: false,
          shouldDirty: true,
        });
        getValues().apportionmentPeriods?.forEach((_, index) =>
          resetField(`apportionmentPeriods.${index}.transactions`),
        );
        if (value === IncomeOrExpenseState.Accrued) {
          setValue('vatRate', 0);
          setValue('fundingSource', undefined);
        }
      },
      onCurrencyCodeChange: (value) => {
        setValue('currencyCode', value as any, {
          shouldValidate: false,
          shouldDirty: true,
        });
        getValues().apportionmentPeriods?.forEach((_, index) =>
          resetField(`apportionmentPeriods.${index}.currencyRate`),
        );
      },
    });
  }, [registerDependentFieldHandlers, setValue, getValues, resetField]);

  const fundingSourceOptions = useFundingSourceOptions(watch('vehicleId'));
  const actionOptions = actions.map((action) => ({
    id: action.id,
    name: buildActionOptionsName(action),
    date: action.ledgerDate,
    key: action.id,
  }));

  const isIncome = incomeOrExpense === IncomeOrExpenseType.Income;

  const incomeOrExpenseTypeOptions = isIncome
    ? typeOptions.incomeTypeOptions
    : typeOptions.expenseTypeOptions;

  const incomeOrExpenseTypeLabel = isIncome ? 'Income type' : 'Expense type';

  const descriptionLabel = isIncome ? 'Income name' : 'Expense name';

  const incomeOrExpenseState = watch('incomeOrExpenseState');
  const isAccrued = incomeOrExpenseState === IncomeOrExpenseState.Accrued;

  const incomeOrExpenseStateLabel =
    incomeOrExpense === 'income' ? 'Income state' : 'Expense state';

  const incomeOrExpenseStateOptions = isIncome
    ? [
        { value: IncomeOrExpenseState.Receivable, label: 'Receivable income' },
        { value: IncomeOrExpenseState.Accrued, label: 'Accrued income' },
      ]
    : [
        { value: IncomeOrExpenseState.Payable, label: 'Payable expense' },
        { value: IncomeOrExpenseState.Accrued, label: 'Accrued expense' },
      ];

  const incomeOrExpenseStateTooltip =
    incomeOrExpense === 'income'
      ? "Use Receivable income when you've issued an invoice and expect to be paid.\n\nUse Accrued income when no invoice has been issued for the income yet."
      : 'Use a Payable expense when you have received an invoice and know the exact amount to pay.\n\nUse an Accrued expense when no invoice has been received for the expense yet.';

  return (
    <Column spacing="lg">
      <FormField size="md">
        <AiFieldHintController
          name="category"
          control={control}
          render={({ field: { ref, onChange, ...rest } }) => (
            <Toggle
              {...rest}
              options={[
                { value: IncomeOrExpenseType.Income, label: 'Income' },
                { value: IncomeOrExpenseType.Expense, label: 'Expense' },
              ]}
              onChange={(value) => {
                onChange(value);
                getValues().apportionmentPeriods?.forEach((_, index) =>
                  resetField(`apportionmentPeriods.${index}.transactions`),
                );
                resetField('incomeOrExpenseType');
                resetField('incomeOrExpenseState');
              }}
              disabled={!!id || isAiScanning}
            />
          )}
        />
      </FormField>

      <FormField size="md">
        <InvoiceUploadSection vehicleId={vehicleId} />
      </FormField>

      <Divider />

      <FormField size="md" label="Vehicle">
        <BasicSelect
          value={vehicleId}
          onChange={() => {}}
          fullWidth
          options={
            availableVehicles?.map((vehicle) => ({
              value: vehicle._id,
              label: vehicle.vehicleName,
            })) ?? []
          }
          placeholder="Select vehicle"
          disabled={true}
        />
      </FormField>
      <FormField size="md" label="Counterparty">
        <AiFieldHintController
          name="counterparty"
          control={control}
          render={({
            fieldState: { error },
            field: { onChange, ...field },
          }) => (
            <CounterpartySelector
              contacts={contacts}
              error={error}
              placeholder="Select counterparty"
              disabled={isSwitchMode || isAiScanning}
              onChange={(value) => {
                if (value) {
                  onChange(value);
                  onCounterpartyChange(value);
                }
              }}
              {...field}
            />
          )}
        />
      </FormField>
      <FormField size="md" label={incomeOrExpenseTypeLabel}>
        <AiFieldHintController
          name="incomeOrExpenseType"
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <BasicSelect
              fullWidth
              disabled={!incomeOrExpense || isSwitchMode || isAiScanning}
              options={incomeOrExpenseTypeOptions}
              placeholder={`Select ${incomeOrExpenseTypeLabel.toLowerCase()}`}
              error={!!error}
              onChange={(value) => {
                onChange(value);
                getValues().apportionmentPeriods?.forEach((_, index) =>
                  resetField(`apportionmentPeriods.${index}.transactions`),
                );
              }}
              helperText={error?.message}
              {...rest}
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        label={incomeOrExpenseStateLabel}
        tooltip={incomeOrExpenseStateTooltip}
      >
        <AiFieldHintController
          name="incomeOrExpenseState"
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <BasicSelect
              fullWidth
              disabled={!incomeOrExpense || isSwitchMode || isAiScanning}
              options={incomeOrExpenseStateOptions}
              placeholder={`Select ${incomeOrExpenseStateLabel.toLowerCase()}`}
              error={!!error}
              onChange={(value) => {
                onChange(value);
                getValues().apportionmentPeriods?.forEach((_, index) =>
                  resetField(`apportionmentPeriods.${index}.transactions`),
                );
                if (value === IncomeOrExpenseState.Accrued) {
                  setValue('vatRate', 0);
                  setValue('fundingSource', undefined);
                }
              }}
              helperText={error?.message}
              {...rest}
            />
          )}
        />
      </FormField>
      <FormField size="md" label="Linked actions">
        <Controller
          name="sourceAllocationActionId"
          control={control}
          render={({ field: { value, onChange } }) => (
            <ActionSelector
              actions={actionOptions}
              placeholder="Select action"
              disabled={isSwitchMode || isAiScanning}
              value={
                value
                  ? actionOptions.find((action) => action.id === value)
                  : null
              }
              onChange={(newValue) => {
                onChange(newValue?.id ?? null);
              }}
            />
          )}
        />
      </FormField>

      {category === IncomeOrExpenseType.Expense && !isAccrued && (
        <FormField
          size="md"
          label="Funding source"
          tooltip="Select whether this action will be paid through general working capital on account or fresh capital that will be part of a future capital call."
        >
          <AiFieldHintController
            name="fundingSource"
            control={control}
            render={({ fieldState: { error }, field: { ref, ...rest } }) => (
              <BasicSelect
                fullWidth
                error={!!error}
                options={fundingSourceOptions.options}
                placeholder="Select funding source"
                helperText={error?.message?.toString()}
                disabled={
                  fundingSourceOptions.isLoading ||
                  (!!id && !isSwitchMode) ||
                  isAiScanning
                }
                {...rest}
              />
            )}
          />
        </FormField>
      )}

      <FormField
        size="md"
        label={
          isAccrued || isSwitchMode ? 'Accrual ledger date' : 'Ledger date'
        }
        tooltip="The date or date range this transaction should be recorded for in the general ledger."
      >
        <AiFieldHintWrapper
          confidence={
            startDateHint?.confidence ??
            apportionmentModeHint?.confidence ??
            endDateHint?.confidence
          }
          reason={
            startDateHint?.reason ??
            apportionmentModeHint?.reason ??
            endDateHint?.reason
          }
        >
          {isSwitchMode ? (
            <StaticField>{watch('startDate')?.toReadable()}</StaticField>
          ) : (
            <LedgerDateInput />
          )}
        </AiFieldHintWrapper>
      </FormField>
      {isSwitchMode && (
        <FormField
          size="md"
          label="Payable ledger date"
          tooltip="The date this transaction should be recorded as payable or receivable in the general ledger."
        >
          <Controller
            name="switchDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                value={field.value ?? null}
                error={!!error}
                helperText={error?.message?.toString()}
                fullWidth
                placeholder="Select payable ledger date"
                minDate={switchDateMinDate}
              />
            )}
          />
        </FormField>
      )}
      <FormField size="md" label="Currency">
        <AiFieldHintController
          name="currencyCode"
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <CurrencySelector
              error={error}
              placeholder="Enter currency"
              disabled={isSwitchMode || isAiScanning}
              onChange={(currency) => {
                onChange(currency);
                getValues().apportionmentPeriods?.forEach((_, index) =>
                  resetField(`apportionmentPeriods.${index}.currencyRate`),
                );
              }}
              {...rest}
            />
          )}
        />
      </FormField>

      <FormField size="md" label="Enter amount (excl. sales tax)">
        <Grid size={{ sm: 12, md: 12, lg: 6 }}>
          <AiFieldHintController
            name="amountExVatTcy"
            control={control}
            render={({
              fieldState: { error },
              field: { ref, onChange, ...rest },
            }) => (
              <NumberInput
                fullWidth
                {...rest}
                onChange={(value) => {
                  onChange(value);
                }}
                decimals={2}
                disabled={isAiScanning}
                error={!!error}
                helperText={error?.message?.toString()}
                placeholder="Enter amount"
              />
            )}
          />
        </Grid>
      </FormField>
      {!isAccrued && (
        <FormField size="md" label="Sales tax (e.g. VAT) rate">
          <AiFieldHintController
            name="vatRate"
            control={control}
            render={({ fieldState: { error }, field }) => (
              <VatRateSelector
                error={error}
                disabled={isAiScanning}
                placeholder="Enter VAT rate"
                {...field}
              />
            )}
          />
        </FormField>
      )}
      <StaticField size="md" label="Gross amount (inc. tax)">
        <Controller
          name="amountIncVatTcy"
          control={control}
          render={({ field: { value } }) => (
            <Typography variant="h6">
              {numberToCurrencyString(value, {
                fallback: '-',
                currency: transactionCurrency,
              })}
            </Typography>
          )}
        />
      </StaticField>

      <FormField size="md" label={descriptionLabel}>
        <AiFieldHintController
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              disabled={isSwitchMode || isAiScanning}
            />
          )}
        />
      </FormField>
    </Column>
  );
};

export default InputDetailsForm;
